import { useMemo, useState } from "react";
import type { RunSummary } from "../lib/types";
import { buildSources, type Source } from "../lib/sources";
import { Mark } from "./Mark";

type Props = {
  run: RunSummary;
  selectedSourceId: string;
  onSelectSource: (id: string) => void;
  query: string;
};

// Column 2 of the 3-column UI: the list of sources within the selected
// run. Each row is either a blog post (with classifier verdict +
// extracted-count badges) or a synthetic group (All packages / GHSA bulk
// / OSV bulk). Clicking a row populates column 3 with that source's
// decisions.
//
// Column header carries the run's deep-link bar (DD run / DD logs / S3
// audit) since these are run-level rather than per-source.
export function SourcesColumn({ run, selectedSourceId, onSelectSource, query }: Props) {
  const sources = useMemo(() => buildSources(run), [run]);

  return (
    <aside className="sources">
      <div className="sources__head">
        <h3>Sources in run {run.shortRunId}</h3>
        <Links run={run} />
      </div>
      <ul className="sources__list">
        {sources.map((s) => (
          <SourceRow
            key={s.id}
            source={s}
            selected={s.id === selectedSourceId}
            onClick={() => onSelectSource(s.id)}
            query={query}
          />
        ))}
      </ul>
    </aside>
  );
}

function Links({ run }: { run: RunSummary }) {
  // Match the existing RunDetail behavior: DD run link omitted if we don't
  // have a per-run URL.
  const hasRunSpecificUrl = run.ddRunUrl && run.ddRunUrlIsRunSpecific !== false;
  return (
    <div className="sources__links">
      {hasRunSpecificUrl && (
        <a
          className="link link--primary"
          href={run.ddRunUrl!}
          target="_blank"
          rel="noreferrer"
          title="Opens this specific run in DD"
        >
          DD run ↗
        </a>
      )}
      <a className="link" href={run.ddLogsUrl} target="_blank" rel="noreferrer">
        Logs ↗
      </a>
      <a className="link" href={run.auditConsoleUrl} target="_blank" rel="noreferrer">
        S3 ↗
      </a>
    </div>
  );
}

function SourceRow({
  source,
  selected,
  onClick,
  query,
}: {
  source: Source;
  selected: boolean;
  onClick: () => void;
  query: string;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <li>
      <button
        className={`source-row source-row--${source.kind} ${selected ? "source-row--selected" : ""}`}
        onClick={onClick}
      >
        <div className="source-row__top">
          <SourceIcon source={source} />
          <span className="source-row__title">
            <Mark text={source.title} query={query} />
          </span>
        </div>
        <div className="source-row__badges">
          {source.kind === "blog-post" && source.classifierCategory && (
            <span className={`tag tag--${source.classifierCategory}`}>
              {source.classifierCategory.replace(/_/g, " ")}
            </span>
          )}
          {source.kind === "blog-post" && source.extractedCount !== null && (
            <span className={`tag tag--extract${source.extractedCount === 0 ? "-zero" : ""}`}>
              extracted {source.extractedCount}
            </span>
          )}
          {source.kind === "blog-post" && source.extractionTruncated && (
            <span className="tag tag--warn">truncated</span>
          )}
          {source.kind === "blog-post" && source.extractionParseError && (
            <span className="tag tag--warn">parse err</span>
          )}
          <span className="source-row__count">
            {source.decisionCount}{" "}
            <span className="source-row__count-label">
              {source.decisionCount === 1 ? "pkg" : "pkgs"}
            </span>
          </span>
          {source.actionableCount > 0 && (
            <span className="source-row__actionable">
              {source.actionableCount} actionable
            </span>
          )}
        </div>
      </button>
      {source.kind === "blog-post" && source.classifierRationale && (
        <button
          className="source-row__expand"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          title={expanded ? "Hide rationale" : "Show classifier rationale"}
        >
          {expanded ? "▾ rationale" : "▸ rationale"}
        </button>
      )}
      {expanded && source.classifierRationale && (
        <p className="source-row__rationale">
          <Mark text={source.classifierRationale} query={query} />
        </p>
      )}
      {source.kind === "blog-post" && source.url && (
        <div className="source-row__url">
          <a href={source.url} target="_blank" rel="noreferrer">
            <Mark text={hostOfUrl(source.url)} query={query} /> ↗
          </a>
        </div>
      )}
    </li>
  );
}

function SourceIcon({ source }: { source: Source }) {
  const map: Record<Source["kind"], string> = {
    "all-packages": "📦",
    "blog-post": "📰",
    "ghsa-bulk": "🛡️",
    "osv-bulk": "🗂️",
  };
  return <span className="source-row__icon">{map[source.kind]}</span>;
}

function hostOfUrl(u: string): string {
  try {
    const url = new URL(u);
    return url.host.replace(/^www\./, "") + url.pathname.replace(/\/$/, "");
  } catch {
    return u;
  }
}
